import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Link from "@mui/material/Link";
import ConfigData from "../config/config.json";
import Tooltip from "@mui/material/Tooltip";

const webserver = ConfigData.WEBSERVER;

const collator = new Intl.Collator("de", { sensitivity: "base" });

export function translateExaminationEnum(enumElement) {
  switch (enumElement) {
    case "ASSIGNED_TO_PROCESS":
      return "Prozess zugewiesen";
    case "IN_PROGRESS":
      return "In Bearbeitung";
    case "NOT_EXAMINED":
      return "Ungeprüft";
    case "PASSED":
      return "Bestanden";
    case "FAILED":
      return "Durchgefallen";
    case "MULTIPLE_FAILURES":
      return "Wiederholt durchgefallen";
    case "PASSED_AFTER_FAILURE":
      return "Wiederholung bestanden";
    default:
      return "Fehler";
  }
}

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (orderBy === "result") {
    //return a[orderBy].localeCompare(b[orderBy], 'de', {sensitivity: 'base'})
    return collator.compare(a[orderBy], b[orderBy]);
  }

  if (orderBy === "examinationDate") {
    if (b[orderBy].substring(6) < a[orderBy].substring(6)) {
      return -1;
    } else if (b[orderBy].substring(6) > a[orderBy].substring(6)) {
      return 1;
    } else {
      if (b[orderBy].substring(3, 6) < a[orderBy].substring(3, 6)) {
        return -1;
      } else if (b[orderBy].substring(3, 6) > a[orderBy].substring(3, 6)) {
        return 1;
      } else {
        if (b[orderBy].substring(0, 3) < a[orderBy].substring(0, 3)) {
          return -1;
        } else if (b[orderBy].substring(0, 3) > a[orderBy].substring(0, 3)) {
          return 1;
        } else {
          return 0;
        }
      }
    }
  }

  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "Prüfungs-Nr.",
  },
  {
    id: "examinationDate",
    numeric: false,
    disablePadding: false,
    label: "Datum",
  },
  {
    id: "result",
    numeric: false,
    disablePadding: false,
    label: "Ergebnis",
  },
  {
    id: "document",
    numeric: false,
    disablePadding: false,
    label: "Original",
  },
  {
    id: "documentCorrection",
    numeric: false,
    disablePadding: false,
    label: "Korrektur",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function ExaminationHistory(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const [rowss, setRowss] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/examResult/getAll/" +
        props.apoID,
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        setRowss(data);
      })
      .catch((error) => {});
  }, [props.apoID]);

  const filteredRows = rowss.filter((row) => {
    return (
      row.id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.examinationDate
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      translateExaminationEnum(row.result)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowss.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={"medium"}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRowss={setRowss}
              rowss={rowss}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rowss.length; found++) {
                    if (rowss[found].id === row.id) {
                      index = found;
                      break;
                    }
                  }
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell id={labelId} scope="row">
                        {row.processID}
                      </TableCell>
                      {/*<TableCell style={{whiteSpace: "normal", wordBreak: "break-word"}} align="left"> {row.descriptionExpanded ? row.email : row.email.substring(0, 65) + " ..."}<IconButton size=''>{row.descriptionExpanded ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}</IconButton></TableCell>*/}
                      <TableCell align="left">{row.examinationDate}</TableCell>
                      <TableCell align="left">
                        {translateExaminationEnum(row.result)}
                      </TableCell>
                      <TableCell align="center">
                        {console.log("FILEPATH: " + row.filePath)}
                        {row.filePath !== null && row.filePath !== "" ? (
                          <Link
                            href={
                              webserver.protocol +
                              "://" +
                              webserver.host +
                              ":" +
                              webserver.port +
                              "/examResult/files/" +
                              row.id
                            }
                          >
                              <Tooltip title={"Dokumentationsbogen herunterladen"} enterDelay={1000}>
                                <IconButton>
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        {row.correctionEmployee !== null &&
                        row.filePathCorrection !== null ? (
                          <Link
                            href={
                              webserver.protocol +
                              "://" +
                              webserver.host +
                              ":" +
                              webserver.port +
                              "/examResult/correctionFiles/" +
                              row.id
                            }
                          >
                            <Tooltip title={"Korrektur herunterladen"} enterDelay={1000}>
                                <IconButton>
                                    <FileDownloadIcon />
                                </IconButton>
                            </Tooltip>
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowss.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
